import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { isFreemiumTier, isFreemiumIntentId } from '@shared';

import type { FreemiumIntentId, FreemiumTier } from '@shared';

export type FreemiumGateStep =
  | 'intent'
  | 'reflexive'
  | 'paywall'
  | 'consult'
  | 'checkout'
  | 'handoffDone';

const STEP_PARAM = 'fxStep';
const INTENT_PARAM = 'fxIntent';
const ANSWER_PARAM = 'fxAnswer';
const TIER_PARAM = 'fxTier';

const VALID_STEPS: FreemiumGateStep[] = [
  'intent',
  'reflexive',
  'paywall',
  'consult',
  'checkout',
  'handoffDone',
];

function isGateStep(value: string | null): value is FreemiumGateStep {
  return !!value && (VALID_STEPS as string[]).includes(value);
}

export function useFreemiumUrlState() {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawStep = searchParams.get(STEP_PARAM);
  const step: FreemiumGateStep = isGateStep(rawStep) ? rawStep : 'intent';

  const rawIntent = searchParams.get(INTENT_PARAM);
  const intent: FreemiumIntentId | null =
    rawIntent && isFreemiumIntentId(rawIntent) ? rawIntent : null;

  const reflexiveAnswer = searchParams.get(ANSWER_PARAM);

  const rawTier = searchParams.get(TIER_PARAM);
  const selectedTier: FreemiumTier | null = rawTier && isFreemiumTier(rawTier) ? rawTier : null;

  const update = useCallback(
    (updates: Record<string, string | null>, options?: { replace?: boolean }) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: options?.replace ?? false },
      );
    },
    [setSearchParams],
  );

  const setIntent = useCallback(
    (next: FreemiumIntentId) => update({ [INTENT_PARAM]: next }, { replace: true }),
    [update],
  );

  const goToIntent = useCallback(
    () => update({ [STEP_PARAM]: 'intent', [ANSWER_PARAM]: null, [TIER_PARAM]: null }),
    [update],
  );

  const goToReflexive = useCallback(() => update({ [STEP_PARAM]: 'reflexive' }), [update]);

  const goToPaywall = useCallback(
    (answer?: string | null) =>
      update({
        [STEP_PARAM]: 'paywall',
        ...(answer !== undefined ? { [ANSWER_PARAM]: answer } : {}),
      }),
    [update],
  );

  const goToConsult = useCallback(() => update({ [STEP_PARAM]: 'consult' }), [update]);

  const startCheckout = useCallback(
    (tier: FreemiumTier) => update({ [STEP_PARAM]: 'checkout', [TIER_PARAM]: tier }),
    [update],
  );

  const goToHandoffDone = useCallback(() => update({ [STEP_PARAM]: 'handoffDone' }), [update]);

  return {
    step,
    intent,
    reflexiveAnswer,
    selectedTier,
    setIntent,
    goToIntent,
    goToReflexive,
    goToPaywall,
    goToConsult,
    startCheckout,
    goToHandoffDone,
  };
}
